import { motion } from 'framer-motion';
import { 
  Building, 
  Globe, 
  Heart, 
  Home, 
  AlertTriangle, 
  Rocket 
} from 'lucide-react';

const industries = [
  {
    icon: Globe,
    title: 'Immigration Law',
    description: 'Full-service immigration support including visas, green cards, work permits, and corporate immigration compliance.',
  },
  {
    icon: Building,
    title: 'Corporate Law Firms',
    description: 'Supporting mergers, acquisitions, corporate governance, and business transactions.',
  },
  {
    icon: Heart,
    title: 'Family Law Practices',
    description: 'Divorce proceedings, custody matters, and family court documentation.',
  },
  {
    icon: Home,
    title: 'Real Estate Attorneys',
    description: 'Title searches, closing documents, and real estate transaction support.',
  },
  {
    icon: AlertTriangle,
    title: 'Personal Injury Firms',
    description: 'Medical records organization, demand letters, and settlement documentation.',
  },
  {
    icon: Rocket,
    title: 'Startups & Legal Depts',
    description: 'Formation documents, equity agreements, and general counsel support.',
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, scale: 0.95 },
  show: { opacity: 1, scale: 1 },
};

export const IndustriesSection = () => {
  return (
    <section
      id="industries"
      className="py-24 md:py-32 relative overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, rgba(217,48,37,0.06) 0%, #f5f5f0 50%, rgba(10,25,47,0.08) 100%)'
      }}
      data-testid="industries-section"
    >
      {/* Gradient orbs */}
      <div className="absolute top-10 left-20 w-56 h-56 bg-[#D93025]/5 rounded-full blur-3xl" />
      <div className="absolute bottom-10 right-20 w-72 h-72 bg-[#0a192f]/5 rounded-full blur-3xl" />
      
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <span className="text-xs font-bold uppercase tracking-widest text-[#D93025]">
            Industries We Serve
          </span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-[#0a192f] mt-4 mb-6">
            Expertise Across Legal Sectors
          </h2>
          <p className="text-base md:text-lg text-[#64748b] max-w-2xl mx-auto">
            Our diverse experience enables us to provide specialized support across 
            multiple practice areas and industries.
          </p>
        </motion.div>

        {/* Industries Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {industries.map((industry, index) => (
            <motion.div
              key={industry.title}
              variants={itemVariants}
              className="bg-white rounded-xl border border-[#e2e2dc] p-6 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1 group"
              data-testid={`industry-card-${index}`}
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#f5f5f0] rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-[#0a192f] transition-colors">
                  <industry.icon className="h-6 w-6 text-[#0a192f] group-hover:text-white transition-colors" />
                </div>
                <div>
                  <h3 className="font-heading text-lg font-medium text-[#0a192f] mb-2">
                    {industry.title}
                  </h3>
                  <p className="text-sm text-[#64748b] leading-relaxed">
                    {industry.description}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
